import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Shield, Check, LogOut } from 'lucide-react';

export default function Paywall() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Please sign in first.');
        setLoading(false);
        return;
      }

      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create checkout');

      // Redirect to Stripe hosted checkout
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3 mb-10">
          <img src="/meridian-logo.png" alt="Meridian" className="w-16 h-16 rounded-xl" />
          <span className="text-2xl font-bold tracking-[0.25em] text-text-primary">MERIDIAN</span>
        </div>

        <div
          className="bg-bg-card rounded-2xl p-8"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-accent" />
            <h2 className="text-xl font-semibold text-text-primary">Subscribe to Meridian</h2>
          </div>
          <p className="text-sm text-text-secondary mb-6">
            Get full access to your personal operating system.
          </p>

          {/* Price */}
          <div className="text-center mb-6">
            <span className="text-4xl font-bold text-text-primary">$5</span>
            <span className="text-text-muted text-sm"> one-time</span>
          </div>

          {/* Features */}
          <ul className="space-y-3 mb-8">
            {[
              'Accounts, credit cards & investments',
              'Cash flow & balance sheet tracking',
              'Paycheck analysis & tax planning',
              'Subscription management',
              'Month-end close workflow',
            ].map((feature) => (
              <li key={feature} className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-success mt-0.5 shrink-0" />
                <span className="text-sm text-text-secondary">{feature}</span>
              </li>
            ))}
          </ul>

          {error && (
            <p className="text-xs text-danger mb-4">{error}</p>
          )}

          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full py-3 text-sm font-semibold bg-accent text-white rounded-xl hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Redirecting to checkout...' : 'Get Access — $5'}
          </button>

          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 text-sm text-text-muted hover:text-text-secondary transition-colors mt-4"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>

        <p className="text-center text-xs text-text-muted mt-6">
          Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );
}
